import React from 'react';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import {
  ClientWebPageCommonTemplateParameters,
  ClientWebPageTemplateParametersDictionary,
} from '@common/model/meta/clientWebPageTemplateParameters';
import { usePageMetaTags } from './useMetaTags';

export type H1RenderConfig = (
  | {
      type: 'common' | 'bold';
      content: string;
    }
  | {
      type: 'br';
    }
)[];

export const useH1 = <P extends ClientWebPage>({
  dictionary,
}: {
  dictionary?: Omit<ClientWebPageTemplateParametersDictionary[P], ClientWebPageCommonTemplateParameters>;
} = {}): H1RenderConfig | undefined => {
  const metaTagConfig = usePageMetaTags({ dictionary, tagNames: ['h1'] })?.[0];

  return React.useMemo(() => {
    const renderConfig: H1RenderConfig = [];

    if (!metaTagConfig?.tagContent) {
      return;
    }

    // 1. Bold text detection
    const re = new RegExp(/\*{2}.*?\*{2}/g);
    let match: RegExpExecArray | null;
    let pos = 0;

    while ((match = re.exec(metaTagConfig.tagContent))) {
      if (pos !== match.index) {
        renderConfig.push({
          type: 'common',
          content: metaTagConfig.tagContent.slice(pos, match.index),
        });
      }

      renderConfig.push({
        type: 'bold',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        content: metaTagConfig.tagContent.slice(match.index + 2, match.index + match[0].length - 2),
      });

      pos = match.index + match[0].length;
    }

    if (pos < metaTagConfig.tagContent.length - 1) {
      renderConfig.push({
        type: 'common',
        content: metaTagConfig.tagContent.slice(pos),
      });
    }

    // 2. Break row detection
    return (
      renderConfig.reduce as <U>(
        callbackfn: (previousValue: U, currentValue: H1RenderConfig[number], currentIndex: number, array: H1RenderConfig) => U,
        initialValue: U,
      ) => U
    )((acc, item) => {
      if (item.type === 'br') {
        acc.push(item);
        return acc;
      }

      const chunks = item.content.split('\n');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const lastChunkIndex = chunks.length - 1;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [index, chunk] of chunks.entries()) {
        if (chunk) {
          acc.push({
            ...item,
            content: chunk,
          });
        }

        // ToDo: temporary disabled
        // if (index !== lastChunkIndex) {
        //   acc.push({
        //     type: 'br',
        //   });
        // }
      }

      return acc;
    }, [] as H1RenderConfig);
  }, [metaTagConfig]);
};
